import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, Pencil, RotateCcw, X } from 'lucide-react';
import { useRef, useState } from 'react';

import { setRecoveryOverride } from '@/api/findings';
import type { FindingOut } from '@/api/types';
import { useAuth } from '@/auth/AuthContext';
import { coerceCurrency, formatCurrency } from '@/lib/format';

/** Inline-editable recovery amount for the flashcard + audit log.
 *
 *  Display mode: formatted currency with a small pencil affordance.
 *  When `finding.recovery_amount_original` is set, renders an "edited"
 *  badge + a reset button to clear the override back to the rule's
 *  number.
 *
 *  Edit mode: numeric input, save on Enter / button click, cancel on
 *  Esc / X. Mutations invalidate the run's findings + run-totals query
 *  keys so the row + summary refresh together. */
export function RecoveryAmountInline({
  finding,
  canReview,
  runId,
  textClassName,
  layout = 'inline',
}: {
  finding: FindingOut;
  canReview: boolean;
  runId: string;
  /** Tailwind classes for the displayed-amount text. The flashcard uses
   *  a large 3xl font; the audit-log uses a compact right-aligned tab. */
  textClassName: string;
  /** `'inline'` (audit row) keeps everything on a single line; `'block'`
   *  (flashcard) puts the "edited" indicator beneath the number so the
   *  big-number visual stays clean. */
  layout?: 'inline' | 'block';
}) {
  const { userKey, getIdToken } = useAuth();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);

  const currency = coerceCurrency(finding.recovery.currency);
  const isOverridden = finding.recovery_amount_original !== null;

  const mutation = useMutation({
    mutationFn: async (next: string | null) =>
      setRecoveryOverride(finding.finding_id, next, (await getIdToken()) ?? ''),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['findings', 'by-run', runId, userKey] });
      qc.invalidateQueries({ queryKey: ['runs', runId, userKey] });
      setEditing(false);
      setDraft('');
    },
  });

  const startEdit = () => {
    if (!canReview || mutation.isPending) return;
    setDraft(finding.recovery.amount);
    setEditing(true);
    // Focus + select-all on the next tick so the practitioner can
    // overtype the existing value in one keystroke.
    queueMicrotask(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    });
  };

  const cancelEdit = () => {
    setEditing(false);
    setDraft('');
  };

  const submit = () => {
    const trimmed = draft.trim();
    if (trimmed.length === 0) return;
    const parsed = Number(trimmed);
    if (Number.isNaN(parsed) || parsed < 0) return;
    // Normalize to 2-decimal string so the server's Numeric(14,2) column
    // doesn't reject a 3-decimal input from the parser. Backend re-
    // quantizes anyway; this keeps the optimistic display honest.
    const normalized = parsed.toFixed(2);
    // Unchanged → just close without a mutation.
    if (normalized === finding.recovery.amount) {
      cancelEdit();
      return;
    }
    mutation.mutate(normalized);
  };

  const clearOverride = () => {
    if (!canReview || mutation.isPending) return;
    mutation.mutate(null);
  };

  if (editing) {
    return (
      <span className="inline-flex items-center gap-1">
        <input
          ref={inputRef}
          type="number"
          inputMode="decimal"
          step="0.01"
          min="0"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submit();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              cancelEdit();
            }
          }}
          disabled={mutation.isPending}
          className="w-32 rounded-md border border-input bg-background px-2 py-1 text-right font-mono text-sm tabular-nums shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-royal disabled:opacity-50"
          aria-label="Recovery amount"
        />
        <button
          type="button"
          onClick={submit}
          disabled={mutation.isPending || draft.trim().length === 0}
          className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-emerald-500/40 bg-white text-emerald-700 hover:bg-emerald-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-royal disabled:cursor-not-allowed disabled:opacity-50"
          title="Save (Enter)"
        >
          <Check className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
        <button
          type="button"
          onClick={cancelEdit}
          disabled={mutation.isPending}
          className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-input bg-white text-brand-slate hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-royal disabled:opacity-50"
          title="Cancel (Esc)"
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </span>
    );
  }

  const displayed = (
    <>
      <span className={textClassName}>{formatCurrency(finding.recovery.amount, currency)}</span>
      {canReview && (
        <button
          type="button"
          onClick={startEdit}
          disabled={mutation.isPending}
          className="ml-2 inline-flex h-6 w-6 items-center justify-center rounded-md text-brand-slate/60 hover:bg-muted/50 hover:text-brand-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-royal disabled:opacity-50"
          title="Edit recovery amount"
        >
          <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      )}
    </>
  );

  const overrideIndicator = isOverridden ? (
    <span className="inline-flex items-center gap-1 text-xs text-amber-700">
      <span
        className="inline-flex items-center rounded-full bg-amber-100 px-1.5 py-0.5 font-medium"
        title={`Manually overridden — rule computed ${formatCurrency(
          finding.recovery_amount_original ?? '0',
          currency,
        )}`}
      >
        edited
      </span>
      {canReview && (
        <button
          type="button"
          onClick={clearOverride}
          disabled={mutation.isPending}
          className="inline-flex h-5 w-5 items-center justify-center rounded text-amber-700 hover:bg-amber-50 disabled:opacity-50"
          title={`Revert to rule-computed ${formatCurrency(
            finding.recovery_amount_original ?? '0',
            currency,
          )}`}
        >
          <RotateCcw className="h-3 w-3" aria-hidden="true" />
        </button>
      )}
    </span>
  ) : null;

  if (layout === 'block') {
    return (
      <span className="inline-flex flex-col items-start gap-1">
        <span className="inline-flex items-center">{displayed}</span>
        {overrideIndicator}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-2">
      {displayed}
      {overrideIndicator}
    </span>
  );
}
